import { Button } from '@sunwebgroup/uds-ui/button'
import { Icon } from '@sunwebgroup/uds-ui/icon'
import { Sheet } from '@sunwebgroup/uds-ui/sheet'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import { type MouseEvent, type ReactElement, useCallback, useState } from 'react'
import { useCMSPageField } from '@/cms/fields'
import { useUser } from '@/domain/user/hooks'
import { useIntl } from '@/intl'
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'
import { useLocation } from '@/scopes/common/infrastructure/dom/location'

import { FavoriteToggleButton } from '@/scopes/favourites/components/FavoriteToggleButton/FavoriteToggleButton'
import { addFavoriteParam } from '@/scopes/favourites/domain/addFavoriteParam'
import { addReturnUrl } from '@/scopes/favourites/domain/addReturnUrl'
import { useFavouriteProduct } from '@/scopes/favourites/domain/hooks'
import styles from './FavoriteButton.module.css'

export function FavoriteButton(): ReactElement {
  const intl = useIntl()
  const { isLoggedIn } = useUser()
  const location = useLocation()
  const [openSheet, setOpenSheet] = useState(false)
  const productId = useCMSPageField('PDP.productId')
  const contextItemId = useCMSPageField('PDP.id')
  const productName = useCMSPageField('PDP.title')
  const collectionName = useCMSPageField('PDP.collectionName')
  const accoStars = useCMSPageField('PDP.rating')
  const rating = useCMSPageField('userReviews.ratingAverage')
  const countryId = useCMSPageField('PDP.countryId')
  const regionId = useCMSPageField('PDP.regionId')
  const cityId = useCMSPageField('PDP.cityId')
  const countryName = useCMSPageField('PDP.countryName')
  const regionName = useCMSPageField('PDP.regionName')
  const cityName = useCMSPageField('PDP.cityName')
  const countryEnglishName = useCMSPageField('PDP.countryEnglishName')
  const regionEnglishName = useCMSPageField('PDP.regionEnglishName')
  const cityEnglishName = useCMSPageField('PDP.cityEnglishName')
  const createAccountUrl: string = useConfiguration('links.createAccount')
  const loginUrl: string = useConfiguration('links.login')
  const { isFavourite, toggleFavouriteState } = useFavouriteProduct(productId, contextItemId)

  const handleClick = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.preventDefault()
      e.stopPropagation()

      if (!isLoggedIn) {
        setOpenSheet(true)
        return
      }

      toggleFavouriteState({
        id: productId || 0,
        name: productName,
        cityId,
        countryId,
        regionId,
        countryName,
        regionName,
        cityName,
        countryEnglishName,
        regionEnglishName,
        cityEnglishName,
        collection: collectionName || '',
        rating,
        userRating: accoStars,
        origin: 'gallery',
      })
    },
    [toggleFavouriteState, isLoggedIn],
  )

  const handleRedirect = (url: string) => {
    const returnUrl = productId
      ? encodeURIComponent(addFavoriteParam(location.pathQueryFragment(), productId))
      : encodeURIComponent(location.pathQueryFragment())
    location.redirectTo(addReturnUrl(url, returnUrl))
    setOpenSheet(false)
  }

  return (
    <>
      <Sheet.Root open={openSheet} onOpenChange={() => setOpenSheet(!openSheet)}>
        <Sheet.Trigger>
          <FavoriteToggleButton
            isFavourite={isFavourite}
            aria-label="Add to favorites"
            onClick={handleClick}
          />
        </Sheet.Trigger>
        <Sheet.Body side="right" hasPadding={false}>
          <Sheet.Header
            closeLabel={intl.formatMessage({ id: 'close' })}
            titleLabel={intl.formatMessage({ id: 'favorite_login_modal_title' })}
          />
          <div className={styles.contentWrapper}>
            <Sheet.Content className={styles.sheetContent}>
              <Icon className={styles.loginIcon} size={48} glyph="Heart" />
              <Typography variant="body-l-bold-snug">
                {intl.formatMessage({ id: 'favorite_login_modal_text' })}
              </Typography>
            </Sheet.Content>
            <Sheet.Footer>
              <div className={styles.footerWrapper}>
                <Button variant="primary" onClick={() => handleRedirect(loginUrl)} fullWidth>
                  {intl.formatMessage({ id: 'favorite_login_modal_primary_action_text' })}
                </Button>
                <Button variant="secondary" onClick={() => handleRedirect(createAccountUrl)} fullWidth>
                  {intl.formatMessage({ id: 'favorite_login_modal_secondary_action_text' })}
                </Button>
              </div>
            </Sheet.Footer>
          </div>
        </Sheet.Body>
      </Sheet.Root>
    </>
  )
}
